import React, { useState, useEffect, useMemo } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
} from "@mui/material";
import paymentportalservice from "../../../services/paymentportalservice";

const BrandsDropdown = ({ control, errors, setValue, getValues, register }) => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);

  const fetchingBrands = async () => {
    setLoading(true);
    setFetchError(null);

    try {
      const response = await paymentportalservice.FetchBrandsData();

      let brandsData = [];

      if (response && response.data) {
        brandsData = response.data;
      } else if (Array.isArray(response)) {
        brandsData = response;
      }

      setBrands(brandsData);
    } catch (error) {
      console.error("Error fetching brands data:", error);
      setFetchError(error.message || "Failed to fetch brands");
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchingBrands();
  }, []);

  const getBrandValue = (brand) => {
    return String(brand.id || brand.brandId || brand._id || "");
  };

  const getBrandName = (brand) => {
    return brand.name || brand.brandName || brand.title || "Unknown Brand";
  };

  const options = useMemo(
    () =>
      brands.map((brand) => ({
        value: getBrandValue(brand),
        label: getBrandName(brand),
      })),
    [brands]
  );

  // Sync pre-filled form value to Autocomplete state (edit mode)
  useEffect(() => {
    const existingValue = getValues("brand");
    if (existingValue && options.length > 0) {
      const match = options.find((opt) => opt.value === existingValue);
      if (match) setSelectedOption(match);
    }
  }, [options]);

  const handleChange = (event, newValue) => {
    setSelectedOption(newValue);
    setValue("brand", newValue ? newValue.value : "", {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <Box sx={{ minWidth: 200, maxWidth: 400, width: "100%" }}>
      <Autocomplete
        fullWidth
        id="brands-select"
        options={options}
        loading={loading}
        disabled={!!fetchError}
        value={selectedOption}
        onChange={handleChange}
        getOptionLabel={(option) => option.label || ""}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        noOptionsText="No brands available"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Brand*"
            error={!!errors.brand || !!fetchError}
            helperText={fetchError || errors.brand?.message}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default BrandsDropdown;
